import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Save, User, Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DirectorPage() {
  const [director, setDirector] = useState({
    name: '',
    designation: '',
    message: '',
    photo_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchDirector();
  }, []);

  useEffect(() => {
    setPreviewUrl(director.photo_url);
  }, [director.photo_url]);

  const fetchDirector = async () => {
    try {
      const token = localStorage.getItem('cish_token');
      const response = await fetch(`${API_URL}/api/director`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data) setDirector(data);
      }
    } catch (error) {
      console.error('Error fetching director:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('cish_token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/files/upload?subfolder=director`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fileUrl = `${API_URL}/api/files/${data.id}`;
        setDirector({ ...director, photo_url: fileUrl });
        toast.success('Image uploaded successfully!');
        setSelectedFile(null);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setDirector({ ...director, photo_url: '' });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('cish_token');
      const response = await fetch(`${API_URL}/api/director`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(director)
      });
      if (response.ok) {
        toast.success('Director information updated successfully!');
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Error saving director information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0E6D2B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <User className="w-6 h-6" />
            Director's Desk
          </h1>
          <p className="text-gray-500 mt-1">Manage the Director's message section on homepage</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-3xl">
          <div className="grid gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Director's Name</label>
              <Input
                value={director.name}
                onChange={(e) => setDirector({ ...director, name: e.target.value })}
                placeholder="Dr. Director Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Designation</label>
              <Input
                value={director.designation}
                onChange={(e) => setDirector({ ...director, designation: e.target.value })}
                placeholder="Director, ICAR-CISH"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Director's Photo</label>

              {/* Preview Image */}
              {previewUrl && (
                <div className="mb-3 relative inline-block">
                  <img
                    src={previewUrl}
                    alt="Director Preview"
                    className="w-32 h-40 object-cover rounded-lg border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error('Failed to load image');
                    }}
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* File Input */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="cursor-pointer"
                  />
                </div>
                {selectedFile && (
                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload
                      </>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select an image file and click Upload. Supported formats: JPG, PNG, WebP
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Director's Message</label>
              <textarea
                value={director.message}
                onChange={(e) => setDirector({ ...director, message: e.target.value })}
                className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] focus:border-transparent"
                placeholder="Enter the director's message..."
              />
            </div>

            <div className="pt-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0E6D2B] hover:bg-[#054018]"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Changes
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
